import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Navbar, Footer } from '../components';
import api from '../utils/api';
import toast from 'react-hot-toast';

const M_CLIENT_KEY = process.env.REACT_APP_MIDTRANS_CLIENT_KEY || "SB-Mid-client-XXXXX";

const Wallet = () => {
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  
  const [balance, setBalance] = useState(0);
  const [activeEvent, setActiveEvent] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterType, setFilterType] = useState('all');

  // Modal states
  const [topupModal, setTopupModal] = useState(false);
  const [withdrawModal, setWithdrawModal] = useState(false);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    
    // Dynamically load Midtrans Snap JS
    const script = document.createElement('script');
    script.src = 'https://app.sandbox.midtrans.com/snap/snap.js'; // Use sandbox for now
    script.setAttribute('data-client-key', M_CLIENT_KEY);
    document.body.appendChild(script);

    fetchBalance();
    fetchTransactions(1);
    
    return () => {
      document.body.removeChild(script);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchTransactions(page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterType]);

  const fetchBalance = async () => {
    try {
      const res = await api.get('/api/wallet/balance');
      setBalance(res.data.balance || 0);
      setActiveEvent(res.data.active_event || null);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchTransactions = async (pageNumber) => {
    setLoading(true);
    try {
      const url = filterType !== 'all' 
        ? `/api/transactions?page=${pageNumber}&type=${filterType}`
        : `/api/transactions?page=${pageNumber}`;
      const res = await api.get(url);
      setTransactions(res.data.data || []);
      setTotalPages(res.data.last_page || 1);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal mengambil data transaksi');
    } finally {
      setLoading(false);
    }
  };

  const formatRupiah = (angka) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(angka || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const handleTopup = async (e) => {
    e.preventDefault();
    if (!amount || amount < 1000) return toast.error("Minimal top-up Rp 1.000");
    
    setSubmitting(true);
    try {
      const res = await api.post('/api/wallet/topup', { amount });
      setTopupModal(false);
      setAmount('');
      
      if (res.data.snap_token) {
        window.snap.pay(res.data.snap_token, {
          onSuccess: function (result) {
            toast.success("Pembayaran berhasil!");
            syncTopup(res.data.transaction.id);
          },
          onPending: function (result) {
            toast.success("Menunggu pembayaran Anda.");
            fetchTransactions(1);
          },
          onError: function (result) {
            toast.error("Pembayaran gagal!");
            fetchTransactions(1);
          },
          onClose: function () {
            toast.error("Anda menutup pop-up sebelum menyelesaikan pembayaran");
            fetchTransactions(1);
          }
        });
      } else {
        toast.info(res.data.message);
        fetchTransactions(1);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal membuat permintaan Top-up');
    } finally {
      setSubmitting(false);
    }
  };

  const syncTopup = async (transactionId) => {
    try {
      await api.post(`/api/wallet/topup/${transactionId}/sync`);
      fetchBalance();
      fetchTransactions(1);
    } catch (err) {
      console.error(err);
      fetchBalance();
      fetchTransactions(1);
    }
  };

  const resumeTopup = async (transactionId) => {
    const toastId = toast.loading("Membuka pembayaran...");
    try {
      const res = await api.post(`/api/wallet/topup/${transactionId}/retake`);
      toast.dismiss(toastId);
      if (res.data.snap_token) {
        window.snap.pay(res.data.snap_token, {
          onSuccess: () => syncTopup(transactionId),
          onPending: () => fetchTransactions(page),
          onError: () => fetchTransactions(page),
          onClose: () => fetchTransactions(page)
        });
      }
    } catch (err) {
      toast.dismiss(toastId);
      toast.error(err.response?.data?.message || 'Gagal melanjutkan Top-up');
    }
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    if (!amount || amount < 10000) return toast.error("Minimal penarikan Rp 10.000");
    if (!description) return toast.error("Informasi rekening wajib diisi");
    
    setSubmitting(true);
    try {
      const res = await api.post('/api/wallet/withdraw', { amount, description });
      toast.success(res.data.message || 'Permintaan ditarik berhasil diajukan');
      setWithdrawModal(false);
      setAmount('');
      setDescription('');
      fetchBalance();
      fetchTransactions(1);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal mengajukan penarikan dana');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'success': return 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20';
      case 'pending': return 'text-amber-400 bg-amber-400/10 border-amber-400/20';
      case 'failed': return 'text-rose-400 bg-rose-400/10 border-rose-400/20';
      default: return 'text-zinc-400 bg-zinc-400/10 border-zinc-400/20';
    }
  };
  
  const getTypeLabel = (type) => {
    const types = {
      topup: 'Top-Up',
      withdrawal: 'Tarik Dana',
      purchase: 'Pembelanjaan',
      refund: 'Pengembalian Dana',
      cashback: 'Bonus Cashback',
      referral_bonus: 'Bonus Referral',
      adjustment: 'Penyesuaian Admin',
      seller_earnings: 'Pendapatan Penjualan'
    };
    return types[type] || type;
  };

  const getAmountColor = (type, amountValue) => {
    if (['topup', 'refund', 'cashback', 'referral_bonus', 'seller_earnings'].includes(type) || (type === 'adjustment' && amountValue > 0)) {
      return 'text-emerald-400';
    }
    return 'text-white'; // deducts
  };

  const getAmountPrefix = (type, amountValue) => {
    if (['topup', 'refund', 'cashback', 'referral_bonus', 'seller_earnings'].includes(type) || (type === 'adjustment' && amountValue > 0)) {
      return '+';
    }
    return '-';
  };

  if (!isAuthenticated) return null;

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-zinc-950 text-white pb-20 pt-10 px-4 sm:px-6 lg:px-8 selection:bg-emerald-500/30">
        <div className="max-w-4xl mx-auto">
          
          <h1 className="text-3xl font-extrabold tracking-tight mb-6">Dompet Ekosistem</h1>

          {/* Balance Card */}
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 border border-zinc-800 rounded-3xl p-6 sm:p-8 shadow-2xl relative overflow-hidden mb-8">
            <div className="absolute top-0 right-0 -mr-8 -mt-8 w-48 h-48 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none"></div>
            
            <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div>
                <p className="text-sm font-medium text-zinc-400 mb-1">Total Saldo Aktif</p>
                <div className="text-4xl sm:text-5xl font-black tracking-tight text-white mb-2">
                  {formatRupiah(balance)}
                </div>
                <div className="text-sm text-zinc-400 flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-emerald-500"></span>
                  Siap digunakan untuk belanja & penarikan
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto mt-4 sm:mt-0">
                <button
                  onClick={() => setTopupModal(true)}
                  className="flex items-center justify-center gap-2 rounded-2xl bg-emerald-500 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-emerald-500/20 hover:bg-emerald-400 hover:shadow-emerald-500/40 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-500"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Isi Saldo
                </button>
                <button
                  onClick={() => setWithdrawModal(true)}
                  className="flex items-center justify-center gap-2 rounded-2xl bg-zinc-800 px-6 py-3 text-sm font-bold text-white border border-zinc-700 hover:bg-zinc-700 hover:text-white transition"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                  Tarik Dana
                </button>
              </div>
            </div>

            {activeEvent && (
              <div className="mt-6 pt-5 border-t border-zinc-800/50 flex items-start gap-4">
                 <div className="flex-shrink-0 p-2 rounded-full bg-amber-500/10 text-amber-500">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                 </div>
                 <div>
                    <h4 className="text-sm font-bold text-white">{activeEvent.name} sedang berlangsung!</h4>
                    <p className="text-xs text-zinc-400 mt-1">
                      Top-up minimal {formatRupiah(activeEvent.min_topup)} dan dapatkan cashback {activeEvent.cashback_percentage}% 
                      {activeEvent.max_cashback ? ` (Maksimal ${formatRupiah(activeEvent.max_cashback)})` : ''}.
                    </p>
                 </div>
              </div>
            )}
          </div>

          {/* Transactions List */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-3xl overflow-hidden shadow-xl">
            <div className="p-6 border-b border-zinc-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <h2 className="text-xl font-bold text-white">Riwayat Transaksi</h2>
              <select
                value={filterType}
                onChange={(e) => { setFilterType(e.target.value); setPage(1); }}
                className="rounded-xl border border-zinc-700 bg-zinc-950 px-4 py-2 text-sm text-zinc-300 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="all">Semua Transaksi</option>
                <option value="topup">Top-Up</option>
                <option value="withdrawal">Tarik Dana</option>
                <option value="purchase">Pembelanjaan</option>
                <option value="refund">Pengembalian Dana</option>
                <option value="cashback">Cashback / Bonus</option>
              </select>
            </div>

            <div className="p-0">
              {loading ? (
                <div className="flex justify-center items-center py-20">
                  <div className="h-8 w-8 border-4 border-zinc-800 border-t-emerald-500 rounded-full animate-spin"></div>
                </div>
              ) : transactions.length === 0 ? (
                <div className="py-20 text-center">
                  <svg className="w-12 h-12 text-zinc-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-zinc-400">Belum ada riwayat transaksi.</p>
                </div>
              ) : (
                <div className="divide-y divide-zinc-800/50">
                  {transactions.map(trx => (
                    <div key={trx.id} className="p-6 hover:bg-zinc-800/20 transition flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <span className="font-semibold text-white">{getTypeLabel(trx.type)}</span>
                          <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border ${getStatusColor(trx.status)}`}>
                            {trx.status}
                          </span>
                        </div>
                        <p className="text-sm text-zinc-400 mb-1">{trx.description}</p>
                        <div className="flex items-center gap-3 text-xs text-zinc-500 font-mono">
                           <span>{trx.reference_id}</span>
                           <span>•</span>
                           <span>{formatDate(trx.created_at)}</span>
                        </div>
                      </div>
                      
                      <div className="flex flex-col items-end gap-2 w-full sm:w-auto">
                        <span className={`text-lg font-bold font-mono whitespace-nowrap ${getAmountColor(trx.type, trx.amount)}`}>
                          {getAmountPrefix(trx.type, trx.amount)} {formatRupiah(Math.abs(trx.amount))}
                        </span>
                        
                        {/* Action buttons for pending transactions */}
                        {trx.status === 'pending' && trx.type === 'topup' && (
                          <button 
                            onClick={() => resumeTopup(trx.id)}
                            className="text-xs px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 font-semibold hover:bg-emerald-500 hover:text-white transition"
                          >
                            Bayar Sekarang
                          </button>
                        )}
                        {trx.status === 'pending' && trx.type !== 'topup' && (
                          <button 
                            className="text-xs px-3 py-1.5 rounded-lg bg-zinc-800 text-zinc-400 font-semibold cursor-default"
                          >
                            Menunggu Review
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 border-t border-zinc-800 bg-zinc-800/30 py-4">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 rounded-xl bg-zinc-900 border border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="text-sm font-medium text-zinc-400 px-4">
                  Hal <span className="text-white">{page}</span> / {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 rounded-xl bg-zinc-900 border border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Topup Modal */}
      {topupModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setTopupModal(false)}></div>
          <div className="relative w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-3xl p-6 shadow-2xl">
            <h3 className="text-xl font-bold text-white mb-4">Top-Up Saldo</h3>
            <form onSubmit={handleTopup}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-zinc-400 mb-2">Nominal Top-up (Rp)</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500 font-medium">Rp</span>
                  <input 
                    type="number" 
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="100000"
                    min="1000"
                    required
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-xl py-3 pl-12 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <p className="text-xs text-zinc-500 mt-2">Minimal top-up Rp 1.000</p>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-6">
                {[50000, 100000, 500000].map(val => (
                  <button 
                    key={val} type="button" 
                    onClick={() => setAmount(val)}
                    className="py-2 text-sm font-medium rounded-lg border border-zinc-700 hover:bg-zinc-800 hover:text-white text-zinc-400 transition"
                  >
                    {formatRupiah(val).replace('Rp', '')}
                  </button>
                ))}
              </div>
              <div className="flex justify-end gap-3 mt-8">
                <button type="button" onClick={() => setTopupModal(false)} className="px-5 py-2.5 rounded-xl text-sm font-bold text-zinc-400 hover:text-white hover:bg-zinc-800 transition">Batal</button>
                <button type="submit" disabled={submitting} className="px-5 py-2.5 rounded-xl text-sm font-bold bg-emerald-500 text-white hover:bg-emerald-400 disabled:opacity-50 transition">
                  {submitting ? 'Memproses...' : 'Lanjutkan Bayar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Withdraw Modal */}
      {withdrawModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setWithdrawModal(false)}></div>
          <div className="relative w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-3xl p-6 shadow-2xl">
            <h3 className="text-xl font-bold text-white mb-4">Tarik Dana</h3>
            <form onSubmit={handleWithdraw}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-zinc-400 mb-2">Nominal Tarik Tunai (Rp)</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500 font-medium">Rp</span>
                  <input 
                    type="number" 
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="50000"
                    min="10000"
                    max={balance}
                    required
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-xl py-3 pl-12 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div className="flex justify-between items-center mt-2">
                   <p className="text-xs text-zinc-500">Minimal Rp 10.000</p>
                   <button type="button" onClick={() => setAmount(balance)} className="text-xs text-emerald-400 hover:underline">Tarik Semua</button>
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-zinc-400 mb-2">Informasi Bank & Rekening</label>
                <textarea 
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Misal: BCA 12345678 a.n John Doe"
                  required
                  rows="3"
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-xl py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                ></textarea>
                <p className="text-xs text-amber-500 mt-2">Pencairan dana membutuhkan proses maksimal 1x24 Jam hari kerja setelah disetujui Admin.</p>
              </div>
              <div className="flex justify-end gap-3 mt-8">
                <button type="button" onClick={() => setWithdrawModal(false)} className="px-5 py-2.5 rounded-xl text-sm font-bold text-zinc-400 hover:text-white hover:bg-zinc-800 transition">Batal</button>
                <button type="submit" disabled={submitting || amount > balance} className="px-5 py-2.5 rounded-xl text-sm font-bold bg-zinc-200 text-black hover:bg-white disabled:opacity-50 transition">
                  {submitting ? 'Mengirim...' : 'Ajukan Tarik Dana'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default Wallet;
